import { useCallback } from 'react';
import { useAssetLoader } from './useAssetLoader';
import useAppContent from '../../../../hooks/useAppContent';
import useAppSettings from '../../../../hooks/useAppSettings';
import level62LogoWhite from '../../../../assets/icons/lvl-62-logo-simple-white.svg';
import level62LogoBlack from '../../../../assets/icons/lvl-62-logo-simple-black.svg';
import spaceforceIconWhite from '../../../../assets/icons/spaceforce-icon-white.svg';
import spaceforceIconBlack from '../../../../assets/icons/spaceforce-icon-black.svg';
import level62IconWhite from '../../../../assets/icons/lvl-62-icon-white.svg';
import level62IconBlack from '../../../../assets/icons/lvl-62-icon-black.svg';
import {
  waitForFontLoad,
  preloadFontForCanvas,
  drawBackground,
  drawCutouts,
  drawLogoWithContainer,
  drawThemedAccents,
  drawPatch,
  drawTextContent,
  getSocialBackgroundColor,
  drawSocialBackground,
  CANVAS_CONFIG
} from '../utils/canvasDrawing';

export const useCanvasDrawing = () => {
  const {
    getLocalImageSrc,
    loadThemedAssets,
    loadPassengerInfoIcons,
    loadPatchImage,
    loadImage
  } = useAssetLoader();
  const { appContent } = useAppContent();
  const { homepageData } = useAppSettings();

  const loadIconSources = useCallback(
    (theme) => {
      return loadPassengerInfoIcons(theme);
    },
    [loadPassengerInfoIcons]
  );

  const drawLogos = useCallback(
    async (ctx, boardingPassData, width, height) => {
      const { MAIN, SPACEFORCE_ICON, LEVEL_62_ICON } = CANVAS_CONFIG.LOGOS;

      const logoSources = {
        main: getLocalImageSrc(
          boardingPassData.backgroundClass === 'space-theme'
            ? level62LogoWhite
            : level62LogoBlack,
          boardingPassData.backgroundClass
        ),
        spaceforce: getLocalImageSrc(
          boardingPassData.backgroundClass === 'space-theme'
            ? spaceforceIconWhite
            : spaceforceIconBlack,
          boardingPassData.backgroundClass
        ),
        level62: getLocalImageSrc(
          boardingPassData.backgroundClass === 'space-theme'
            ? level62IconWhite
            : level62IconBlack,
          boardingPassData.backgroundClass
        )
      };

      try {
        if (logoSources.main) {
          const mainLogo = await loadImage(logoSources.main);
          drawLogoWithContainer(
            ctx,
            mainLogo,
            MAIN.CONTAINER.X_POSITION,
            MAIN.CONTAINER.Y_POSITION,
            { CONTAINER: MAIN.CONTAINER },
            { IMAGE: { ...MAIN.IMAGE, calculateWidth: true } },
            boardingPassData
          );
        }

        if (logoSources.spaceforce) {
          const spaceforceIcon = await loadImage(logoSources.spaceforce);
          drawLogoWithContainer(
            ctx,
            spaceforceIcon,
            SPACEFORCE_ICON.CONTAINER.X_POSITION,
            SPACEFORCE_ICON.CONTAINER.Y_POSITION,
            { CONTAINER: SPACEFORCE_ICON.CONTAINER },
            { IMAGE: SPACEFORCE_ICON.IMAGE },
            boardingPassData
          );
        }

        if (logoSources.level62) {
          const level62Icon = await loadImage(logoSources.level62);
          drawLogoWithContainer(
            ctx,
            level62Icon,
            LEVEL_62_ICON.CONTAINER.X_POSITION,
            LEVEL_62_ICON.CONTAINER.Y_POSITION,
            { CONTAINER: LEVEL_62_ICON.CONTAINER },
            { IMAGE: LEVEL_62_ICON.IMAGE },
            boardingPassData
          );
        }
      } catch (error) {
        // Logo loading failed
      }
    },
    [getLocalImageSrc, loadImage]
  );

  const drawSeparatorAndFooter = useCallback(
    (ctx, boardingPassData, width, height, cutoutY, textColor) => {
      const separatorConfig = {
        y: cutoutY,
        dashPattern: [4, 4],
        lineWidth: 1,
        colors: {
          'space-theme': 'rgba(14, 16, 69, 1)',
          'earth-theme': 'rgba(214, 84, 53, 1)',
          'default-theme': textColor
        }
      };

      const cutoutRadius = CANVAS_CONFIG.CUTOUT_RADIUS;
      const lineColor =
        separatorConfig.colors[boardingPassData.backgroundClass] ||
        separatorConfig.colors['default-theme'];

      ctx.strokeStyle = lineColor;
      ctx.setLineDash(separatorConfig.dashPattern);
      ctx.lineWidth = separatorConfig.lineWidth;
      ctx.beginPath();
      ctx.moveTo(cutoutRadius, separatorConfig.y);
      ctx.lineTo(width - cutoutRadius, separatorConfig.y);
      ctx.stroke();
      ctx.setLineDash([]);
    },
    []
  );

  const drawBoardingPassToCanvas = useCallback(
    async (canvas, boardingPassData) => {
      const ctx = canvas.getContext('2d', { alpha: true });

      const {
        WIDTH: baseWidth,
        HEIGHT: baseHeight,
        BOARDING_PASS_WIDTH: baseBPWidth,
        BOARDING_PASS_HEIGHT: baseBPHeight,
        PADDING: basePadding,
        SCALE_FACTOR
      } = CANVAS_CONFIG;

      const scale = SCALE_FACTOR || 1;
      const canvasWidth = baseWidth * scale;
      const canvasHeight = baseHeight * scale;

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      ctx.scale(scale, scale);
      ctx.clearRect(0, 0, baseWidth, baseHeight);

      const [normalFontLoaded, mediumFontLoaded] = await Promise.all([
        waitForFontLoad('TTFirsNeueNormal', 5000),
        waitForFontLoad('TTFirsNeueMedium', 5000)
      ]);

      if (normalFontLoaded) {
        await preloadFontForCanvas('TTFirsNeueNormal');
      }
      if (mediumFontLoaded) {
        await preloadFontForCanvas('TTFirsNeueMedium');
      }

      const primaryFont = normalFontLoaded ? 'TTFirsNeueNormal' : 'Arial';
      const textColor =
        boardingPassData.backgroundClass === 'space-theme'
          ? '#F8F2EE'
          : '#000000';

      drawSocialBackground(
        ctx,
        boardingPassData.backgroundClass,
        baseWidth,
        baseHeight,
        basePadding
      );

      const socialBgColor = getSocialBackgroundColor(
        boardingPassData.backgroundClass
      );

      ctx.save();
      ctx.translate(basePadding, basePadding);

      ctx.beginPath();
      ctx.roundRect(
        0,
        0,
        baseBPWidth,
        baseBPHeight,
        CANVAS_CONFIG.BORDER_RADIUS
      );
      ctx.clip();

      drawBackground(ctx, boardingPassData, baseBPWidth, baseBPHeight);

      try {
        const themedAssets = await loadThemedAssets(
          boardingPassData.backgroundClass
        );

        ctx.save();
        await drawThemedAccents(
          ctx,
          themedAssets,
          baseBPWidth,
          baseBPHeight,
          boardingPassData.backgroundClass
        );
        ctx.restore();
      } catch (error) {
        // Themed accents failed to load
      }

      const cutoutY = 405;

      await drawLogos(ctx, boardingPassData, baseBPWidth, baseBPHeight);

      const iconSources = loadIconSources(boardingPassData.backgroundClass);
      await drawTextContent(
        ctx,
        boardingPassData,
        appContent,
        baseBPWidth,
        baseBPHeight,
        primaryFont,
        textColor,
        iconSources,
        loadImage
      );

      await drawPatch(
        ctx,
        boardingPassData,
        loadPatchImage,
        baseBPWidth,
        baseBPHeight
      );

      drawSeparatorAndFooter(
        ctx,
        boardingPassData,
        baseBPWidth,
        baseBPHeight,
        cutoutY,
        textColor
      );

      drawCutouts(ctx, baseBPWidth, baseBPHeight, socialBgColor);

      ctx.restore();
    },
    [
      loadThemedAssets,
      loadPatchImage,
      loadImage,
      appContent,
      loadIconSources,
      drawLogos,
      drawSeparatorAndFooter
    ]
  );

  return {
    drawBoardingPassToCanvas
  };
};
